import React, { useState, useEffect } from 'react';
import { Car, DollarSign, Percent, TrendingUp } from 'lucide-react';
import Card from '../../components/UI/Card';
import CalculatorInput from '../../components/Tools/CalculatorInput';
import useTranslation from '../../hooks/useTranslation';

const makes = [
  'Acura', 'Audi', 'BMW', 'Buick', 'Cadillac', 'Chevrolet', 'Chrysler',
  'Dodge', 'FIAT', 'Ford', 'Genesis', 'GMC', 'Honda', 'Hyundai', 'INFINITI',
  'Jaguar', 'Jeep', 'Kia', 'Land Rover', 'Lexus', 'Lincoln', 'Mazda',
  'Mercedes-Benz', 'MINI', 'Nissan', 'Porsche', 'Ram', 'Toyota', 'Volkswagen', 'Volvo'
];

const FinanceAnalyzer = () => {
  const [inputs, setInputs] = useState({
    make: makes[0],
    msrp: '',
    purchasePrice: '',
    options: '',
    loanTerm: '60',
    apr: '5.5',
    salesTaxRate: '',
    cashDown: '',
    tradeInValue: '',
    tradeInPayoff: '',
    rebates: '',
    dealerFees: '',
    registrationFees: '',
    warranty: '',
    otherUpfront: '',
    expectedResaleValue: '',
    holdMonths: '60'
  });

  const [results, setResults] = useState({
    monthlyPayment: 0,
    loanAmount: 0,
    totalInterest: 0,
    totalPaid: 0,
    salesTax: 0,
    dueAtSigning: 0,
    cashDown: 0,
    otherUpfront: 0,
    netTrade: 0,
    remainingBalance: 0,
    expectedEquity: 0,
    holdMonths: 0,
    dealScore: '',
    dealColor: '',
    dealMsg: ''
  });
  const { t } = useTranslation();

  const handleInputChange = (field, value) => {
    setInputs((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCurrency = (value) => {
    const numericValue = Number(value);
    const safeValue = Number.isFinite(numericValue) ? Math.round(numericValue) : 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(safeValue);
  };

  useEffect(() => {
    calculateFinance();
    // eslint-disable-next-line
  }, [inputs]);

  const calculateFinance = () => {
    const msrp = parseFloat(inputs.msrp) || 0;
    const purchasePriceInput = parseFloat(inputs.purchasePrice);
    const options = parseFloat(inputs.options) || 0;
    const purchasePrice = Number.isFinite(purchasePriceInput) ? purchasePriceInput : msrp;
    const basePrice = Math.max(purchasePrice + options, 0);

    const loanTerm = parseInt(inputs.loanTerm, 10) || 0;
    const apr = parseFloat(inputs.apr) || 0;
    const salesTaxRate = parseFloat(inputs.salesTaxRate) || 0;

    const cashDown = Math.max(parseFloat(inputs.cashDown) || 0, 0);
    const tradeInValue = Math.max(parseFloat(inputs.tradeInValue) || 0, 0);
    const tradeInPayoff = Math.max(parseFloat(inputs.tradeInPayoff) || 0, 0);
    const rebates = Math.max(parseFloat(inputs.rebates) || 0, 0);
    const dealerFees = Math.max(parseFloat(inputs.dealerFees) || 0, 0);
    const registrationFees = Math.max(parseFloat(inputs.registrationFees) || 0, 0);
    const warranty = Math.max(parseFloat(inputs.warranty) || 0, 0);
    const otherUpfront = Math.max(parseFloat(inputs.otherUpfront) || 0, 0);
    const expectedResaleValue = Math.max(parseFloat(inputs.expectedResaleValue) || 0, 0);
    const requestedHoldMonths = parseInt(inputs.holdMonths, 10) || 0;

    const netTrade = tradeInValue - tradeInPayoff;
    const taxableTradeReduction = Math.max(netTrade, 0);
    const taxableAmount = Math.max(basePrice - rebates - taxableTradeReduction, 0);
    const salesTax = taxableAmount * (salesTaxRate / 100);

    const financedFees = dealerFees + registrationFees + warranty;
    const loanAmountBeforeClamp = basePrice - cashDown - netTrade - rebates + financedFees + salesTax;
    const loanAmount = Math.max(loanAmountBeforeClamp, 0);

    const hasFinanceInputs = basePrice > 0 && loanTerm > 0 && apr >= 0;

    if (!hasFinanceInputs) {
      const dueAtSigning = cashDown + otherUpfront;
      setResults({
        monthlyPayment: 0,
        loanAmount: 0,
        totalInterest: 0,
        totalPaid: 0,
        salesTax,
        dueAtSigning,
        cashDown,
        otherUpfront,
        netTrade,
        remainingBalance: 0,
        expectedEquity: 0,
        holdMonths: 0,
        dealScore: '',
        dealColor: '',
        dealMsg: ''
      });
      return;
    }

    const monthlyRate = apr > 0 ? (apr / 100) / 12 : 0;
    let monthlyPayment = 0;

    if (loanAmount > 0) {
      if (monthlyRate > 0) {
        const factor = Math.pow(1 + monthlyRate, loanTerm);
        monthlyPayment = loanAmount * ((monthlyRate * factor) / (factor - 1));
      } else {
        monthlyPayment = loanAmount / loanTerm;
      }
    }

    const totalPaid = monthlyPayment * loanTerm;
    const totalInterest = Math.max(totalPaid - loanAmount, 0);

    const dueAtSigning = cashDown + otherUpfront + monthlyPayment;

    const holdMonths = Math.min(Math.max(requestedHoldMonths, 0), loanTerm || requestedHoldMonths);
    let remainingBalance = 0;
    if (loanAmount > 0 && holdMonths > 0) {
      if (monthlyRate > 0) {
        const factor = Math.pow(1 + monthlyRate, holdMonths);
        remainingBalance = loanAmount * factor - monthlyPayment * ((factor - 1) / monthlyRate);
      } else {
        remainingBalance = Math.max(loanAmount - monthlyPayment * holdMonths, 0);
      }
      remainingBalance = Math.max(remainingBalance, 0);
    }

    const expectedEquity = expectedResaleValue > 0 ? expectedResaleValue - remainingBalance : 0;

    let dealScore = 'financeAnalyzer.deal.average';
    let dealColor = 'bg-yellow-100 text-yellow-800';
    let dealMsg = 'financeAnalyzer.deal.msg.average';

    if (apr <= 4 && totalInterest <= loanAmount * 0.15) {
      dealScore = 'financeAnalyzer.deal.great';
      dealColor = 'bg-green-100 text-green-800';
      dealMsg = 'financeAnalyzer.deal.msg.great';
    } else if (apr >= 7 || totalInterest > loanAmount * 0.3) {
      dealScore = 'financeAnalyzer.deal.bad';
      dealColor = 'bg-red-100 text-red-800';
      dealMsg = 'financeAnalyzer.deal.msg.bad';
    }

    setResults({
      monthlyPayment,
      loanAmount,
      totalInterest,
      totalPaid: totalPaid + cashDown + otherUpfront,
      salesTax,
      dueAtSigning,
      cashDown,
      otherUpfront,
      netTrade,
      remainingBalance,
      expectedEquity,
      holdMonths,
      dealScore,
      dealColor,
      dealMsg
    });
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Car className="h-5 w-5 text-primary-600" />
          {t('financeAnalyzer.title')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('financeAnalyzer.description')}
        </p>
      </Card>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Car className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('financeAnalyzer.section.vehicle')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{t('financeAnalyzer.input.make')}</label>
                <select
                  className="w-full rounded border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 dark:bg-dark-900 dark:text-white p-2 bg-white"
                  value={inputs.make}
                  onChange={(e) => handleInputChange('make', e.target.value)}
                >
                  {makes.map((make) => (
                    <option key={make} value={make}>
                      {make}
                    </option>
                  ))}
                </select>
              </div>
              <CalculatorInput
                label={t('financeAnalyzer.input.msrp')}
                value={inputs.msrp}
                onChange={(value) => handleInputChange('msrp', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('financeAnalyzer.input.vehiclePrice')}
                value={inputs.purchasePrice}
                onChange={(value) => handleInputChange('purchasePrice', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('financeAnalyzer.input.options')}
                value={inputs.options}
                onChange={(value) => handleInputChange('options', value)}
                prefix="$"
              />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Percent className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('financeAnalyzer.section.financing')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <CalculatorInput
                label={t('financeAnalyzer.input.loanTermMonths')}
                value={inputs.loanTerm}
                onChange={(value) => handleInputChange('loanTerm', value)}
                type="number"
              />
              <CalculatorInput
                label={t('financeAnalyzer.input.apr')}
                value={inputs.apr}
                onChange={(value) => handleInputChange('apr', value)}
                type="number"
                placeholder="5.5"
              />
              <CalculatorInput
                label={t('financeAnalyzer.input.salesTaxRate')}
                value={inputs.salesTaxRate}
                onChange={(value) => handleInputChange('salesTaxRate', value)}
                type="number"
              />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('financeAnalyzer.section.adjustments')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <CalculatorInput
                label={t('financeAnalyzer.input.cashDown')}
                value={inputs.cashDown}
                onChange={(value) => handleInputChange('cashDown', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('financeAnalyzer.input.tradeInValue')}
                value={inputs.tradeInValue}
                onChange={(value) => handleInputChange('tradeInValue', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('financeAnalyzer.input.tradeInPayoff')}
                value={inputs.tradeInPayoff}
                onChange={(value) => handleInputChange('tradeInPayoff', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('financeAnalyzer.input.rebates')}
                value={inputs.rebates}
                onChange={(value) => handleInputChange('rebates', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('financeAnalyzer.input.dealerFees')}
                value={inputs.dealerFees}
                onChange={(value) => handleInputChange('dealerFees', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('financeAnalyzer.input.registrationFees')}
                value={inputs.registrationFees}
                onChange={(value) => handleInputChange('registrationFees', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('financeAnalyzer.input.warranty')}
                value={inputs.warranty}
                onChange={(value) => handleInputChange('warranty', value)}
                prefix="$"
              />
              <CalculatorInput
                label={t('financeAnalyzer.input.otherUpfront')}
                value={inputs.otherUpfront}
                onChange={(value) => handleInputChange('otherUpfront', value)}
                prefix="$"
              />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('financeAnalyzer.section.equity')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <CalculatorInput
                label={t('financeAnalyzer.input.holdMonths')}
                value={inputs.holdMonths}
                onChange={(value) => handleInputChange('holdMonths', value)}
                type="number"
              />
              <CalculatorInput
                label={t('financeAnalyzer.input.resaleValue')}
                value={inputs.expectedResaleValue}
                onChange={(value) => handleInputChange('expectedResaleValue', value)}
                prefix="$"
              />
            </div>
          </Card>
        </div>

        <div className="w-full lg:w-96 flex-shrink-0 lg:sticky lg:top-20 lg:self-start">
          <Card className="p-6 space-y-4">
            <div className="text-center">
              <h3 className="text-lg font-semibold uppercase tracking-wide">{t('financeAnalyzer.summary.title')}</h3>
            </div>
            {results.dealScore && (
              <>
                <div className={`rounded px-3 py-2 text-center font-semibold text-sm ${results.dealColor}`}>
                  {t(results.dealScore)}
                </div>
                <div className="text-xs text-gray-500 text-center mb-2">{t(results.dealMsg)}</div>
              </>
            )}
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('financeAnalyzer.summary.monthlyPayment')}</div>
              <div className="bg-primary-100 text-primary-900 text-2xl font-bold px-4 py-2 rounded text-center">
                {formatCurrency(results.monthlyPayment)}
              </div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('financeAnalyzer.summary.loanAmount')}</div>
              <div className="text-2xl font-bold text-center">
                {formatCurrency(results.loanAmount)}
              </div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('financeAnalyzer.summary.dueAtSigning')}</div>
              <div className="bg-primary-100 text-primary-900 text-xl font-bold px-4 py-2 rounded text-center">
                {formatCurrency(results.dueAtSigning)}
              </div>
              <div className="text-xs text-gray-500 mt-2 space-y-1">
                <div>{t('financeAnalyzer.summary.firstPayment')}: <span className="font-semibold">{formatCurrency(results.monthlyPayment)}</span></div>
                <div>{t('financeAnalyzer.summary.cashDown')}: <span className="font-semibold">{formatCurrency(results.cashDown)}</span></div>
                <div>{t('financeAnalyzer.summary.otherUpfront')}: <span className="font-semibold">{formatCurrency(results.otherUpfront)}</span></div>
              </div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('financeAnalyzer.summary.salesTax')}</div>
              <div className="text-lg font-semibold text-center">
                {formatCurrency(results.salesTax)}
              </div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('financeAnalyzer.summary.totalInterest')}</div>
              <div className="text-xl font-bold text-center text-primary-700 dark:text-primary-400">
                {formatCurrency(results.totalInterest)}
              </div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('financeAnalyzer.summary.totalOutlay')}</div>
              <div className="text-xl font-bold text-center">
                {formatCurrency(results.totalPaid)}
              </div>
            </div>
            {results.holdMonths > 0 && (
              <div className="pt-2 border-t border-gray-100 dark:border-dark-700">
                <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">{t('financeAnalyzer.summary.equityForecast', '', { month: results.holdMonths })}</div>
                <div className="text-xs text-gray-500 space-y-1">
                  <div>{t('financeAnalyzer.summary.remainingBalance')}: <span className="font-semibold">{formatCurrency(results.remainingBalance)}</span></div>
                  <div>{t('financeAnalyzer.summary.projectedEquity')}: <span className={`font-semibold ${results.expectedEquity >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {formatCurrency(results.expectedEquity)}
                  </span></div>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FinanceAnalyzer;
